// src/services/newspaperApi.js — client for /api/newspaper/* (new newspaper system)
import { API_BASE } from "./apiClient";

async function call(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

const json = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body || {}),
});

// absolute URL for links returned by the API (files, PDFs)
export const apiUrl = (path) => (path ? `${API_BASE}${path}` : "");

export const CATEGORIES = ["ประกาศประชุม", "ประกาศบริษัท", "ข่าวสารองค์กร", "บทความธุรกิจ", "ความรู้บัญชีและระบบ", "อื่น ๆ"];

export const STATUS_LABEL = {
  submitted: "รอชำระเงิน",
  paid: "ชำระแล้ว รอตรวจทาน",
  approved: "อนุมัติแล้ว",
  rejected: "ไม่อนุมัติ",
};

// ── public ──
export const submitAnnouncement = (formData) => call("/api/newspaper/submissions", { method: "POST", body: formData });
export const trackAnnouncement = (code, phone) => call("/api/newspaper/track", json("POST", { code, phone }));
// several notices → one payment: { order: { orderCode, count, copies, total, items[] } } (the server computes the total)
export const createOrder = (codes, phone, copies) => call("/api/newspaper/orders", json("POST", { codes, phone, copies }));
export const adminConfirmOrder = (orderCode) => call(`/api/newspaper/admin/orders/${encodeURIComponent(orderCode)}`, json("PATCH", { status: "paid" }));
// payment slip for a notice — or a whole order (order code) — that waits for payment (code + phone, like tracking)
export const sendPaymentSlip = (code, phone, file) => {
  const fd = new FormData();
  fd.append("code", code);
  fd.append("phone", phone);
  fd.append("slip", file, file.name);
  return call("/api/newspaper/payment-slip", { method: "POST", body: fd });
};
export const getPublished = (q = "", category = "") =>
  call(`/api/newspaper/published?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}`);
export const getEditions = (days = 14) => call(`/api/newspaper/editions?days=${days}`);
// editions from..to (ISO dates, newest first) — "ฉบับย้อนหลัง" date range
export const getEditionRange = (from, to) =>
  call(`/api/newspaper/editions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
export const getEdition = (date) => call(`/api/newspaper/editions/${encodeURIComponent(date)}`);
export const editionPdfUrl = (date) => apiUrl(`/api/newspaper/editions/${date}/pdf`);

// news / PR articles (public)
export const getArticles = (limit = 4) => call(`/api/newspaper/articles?limit=${limit}`);
export const getArticle = (id) => call(`/api/newspaper/articles/${encodeURIComponent(id)}`);

// ── admin ──
export const ARTICLE_STATUS_LABEL = { draft: "ร่าง", published: "เผยแพร่" };
export const adminArticles = (status = "", q = "") =>
  call(`/api/newspaper/admin/articles?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`);
export const adminCreateArticle = (formData) => call("/api/newspaper/admin/articles", { method: "POST", body: formData });
export const adminUpdateArticle = (id, formData) => call(`/api/newspaper/admin/articles/${id}`, { method: "PATCH", body: formData });
// Word (.docx) → { title, body } for the article form (nothing is saved)
export const adminImportWord = (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return call("/api/newspaper/admin/articles/import-word", { method: "POST", body: fd });
};
// PromptPay QR picture for the payment box → { url } (then saved in site content paymentInfo.qrUrl)
export const adminUploadPaymentQr = (file) => {
  const fd = new FormData();
  fd.append("qr", file, file.name);
  return call("/api/newspaper/admin/payment-qr", { method: "POST", body: fd });
};
// admin notifications: e-mail recipients (private) + LINE status, and a test send
export const adminNotifySettings = () => call("/api/newspaper/admin/notify-settings");
export const adminSaveNotifySettings = (emails) => call("/api/newspaper/admin/notify-settings", json("PUT", { emails }));
export const adminNotifyTest = () => call("/api/newspaper/admin/notify-test", json("POST", {}));
// closed editions that must be printed and kept (owner rule) — GET list, PATCH printed / not printed
export const adminPrintLog = () => call("/api/newspaper/admin/print-log");
export const adminSetPrinted = (date, printed = true) =>
  call(`/api/newspaper/admin/print-log/${date}`, json("PATCH", { printed }));
export const adminSetPrintedMany = (dates, printed = true) =>
  call("/api/newspaper/admin/print-log", json("PATCH", { dates, printed }));
export const adminDeleteArticle = (id) => call(`/api/newspaper/admin/articles/${id}`, { method: "DELETE" });
export const adminList = (status = "", q = "") =>
  call(`/api/newspaper/admin/submissions?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`);
export const adminUpdate = (id, patch) => call(`/api/newspaper/admin/submissions/${id}`, json("PATCH", patch));
export const adminDelete = (id) => call(`/api/newspaper/admin/submissions/${id}`, { method: "DELETE" });
export const cleanupPreview = (params) => call("/api/newspaper/admin/cleanup/preview", json("POST", params));
export const cleanupRun = (params) => call("/api/newspaper/admin/cleanup", json("POST", { ...params, confirm: true }));
